import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject, Subscription, map, switchMap, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NotificationApiService } from '../../services/notification-api.service';
import { DataEventNotificationService } from '../../services/data-event-notification.service';
import { ShellOverlayService } from '../../services/shell-overlay.service';
import { PortalNotification } from '../../domain/portal-notification.model';
import { PortalAuthService } from '../../auth/portal-auth.service';
import { IonicModule } from '@ionic/angular';
import { AsyncPipe, DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Notification bell + dropdown panel. Owns its own list/badge state and the
 * real-time push subscription. Coordinated with the profile popover via
 * ShellOverlayService. Host uses display:contents so it sits transparently inside
 * the toolbar's ion-buttons.
 */
@Component({
    selector: 'app-notifications-center',
    templateUrl: './notifications-center.component.html',
    styleUrls: ['./notifications-center.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        AsyncPipe,
        DatePipe,
        TranslatePipe,
    ],
})
export class NotificationsCenterComponent implements OnInit, OnDestroy {
  readonly unreadCount$ = this.notificationApi.unreadCount$;
  readonly isOpen$ = this.overlay.open$.pipe(map(p => p === 'notifications'));
  notifications: PortalNotification[] = [];

  private readonly destroy$ = new Subject<void>();
  private pushSub?: Subscription;

  /** Auth0 sub of the signed-in member; null until the profile resolves. */
  private currentUserSub: string | null = null;

  constructor(
    private readonly notificationApi: NotificationApiService,
    private readonly dataEventNotifications: DataEventNotificationService,
    private readonly overlay: ShellOverlayService,
    private readonly portalAuth: PortalAuthService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.portalAuth.user$.pipe(takeUntil(this.destroy$))
      .subscribe(user => { this.currentUserSub = user?.sub ?? null; });

    // Load the list whenever the panel opens.
    this.overlay.open$.pipe(
      filter(p => p === 'notifications'),
      switchMap(() => this.notificationApi.getAll()),
      takeUntil(this.destroy$),
    ).subscribe(n => { this.notifications = n; this.cdr.markForCheck(); });

    // Real-time push: prepend notification and increment the badge.
    this.pushSub = this.dataEventNotifications.notification$.subscribe(n => {
      this.notifications = [n, ...this.notifications];
      // You just did this yourself — badging it would light up the bell on your own save.
      // The entry still joins the list; only the count skips it, which is the same rule the
      // server applies to GET /notifications/unread and NavBadgeService to the side menu.
      if (!this.isOwnAction(n)) {
        this.notificationApi.unreadCount$.next(this.notificationApi.unreadCount$.value + 1);
      }
      this.cdr.markForCheck();
    });
  }

  /** True when this member caused the notification. System events have no issuedBy and never match. */
  private isOwnAction(n: PortalNotification): boolean {
    return !!this.currentUserSub && n.issuedBy === this.currentUserSub;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pushSub?.unsubscribe();
  }

  close(): void { this.overlay.close(); }

  markRead(id: string): void {
    this.notificationApi.markRead(id).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.notifications = this.notifications.map(n =>
        n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n);
      this.cdr.markForCheck();
    });
  }

  markAllRead(): void {
    this.notificationApi.markAllRead().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.notifications = this.notifications.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() }));
      this.cdr.markForCheck();
    });
  }

  notificationIcon(type: string): string {
    switch (type) {
      case 'GatewayOffline':
      case 'GatewayOnline':
      case 'GatewayRegistered':
      case 'GatewayUpdated':
      case 'GatewayDeleted':
      case 'GatewayMetricsUpdated': return 'hardware-chip-outline';

      case 'GatewayVpnConnected':
      case 'GatewayVpnDisconnected': return 'lock-closed-outline';

      case 'CommandFailed': return 'alert-circle-outline';

      case 'InvoiceOverdue':
      case 'InvoiceCreated':
      case 'InvoiceUpdated':
      case 'InvoicePaid':
      case 'RechnungCreated':
      case 'RechnungUpdated':
      case 'RechnungDeleted': return 'receipt-outline';

      case 'ReservationCreated':
      case 'ReservationAccepted':
      case 'ReservationRejected':
      case 'ReservationCancelled': return 'calendar-outline';

      case 'MenuCreated':
      case 'MenuUpdated':
      case 'MenuDeleted': return 'restaurant-outline';

      case 'SubscriptionCreated':
      case 'SubscriptionUpdated':
      case 'SubscriptionDeleted': return 'card-outline';

      case 'PermissionsUpdated': return 'key-outline';

      case 'TableStackSettingsUpdated': return 'settings-outline';

      case 'TicketReply':
      case 'TicketCreated':
      case 'TicketUpdated': return 'chatbubbles-outline';

      case 'MemberInvited':
      case 'MemberUpdated':
      case 'MemberRevoked': return 'people-outline';

      case 'RoleCreated':
      case 'RoleUpdated':
      case 'RoleDeleted': return 'shield-checkmark-outline';

      case 'JobPositionCreated':
      case 'JobPositionUpdated':
      case 'JobPositionDeleted': return 'briefcase-outline';

      case 'DepartmentCreated':
      case 'DepartmentUpdated':
      case 'DepartmentDeleted': return 'git-branch-outline';

      case 'TenantCreated':
      case 'TenantUpdated':
      case 'TenantDeactivated': return 'business-outline';

      default: return 'notifications-outline';
    }
  }
}
